import React, { useState } from 'react';
import { faRunning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AssignmentModel,
  AssignmentRequestStatusEnum,
  AssignmentStatusEnum,
  CaseModel,
  CaseTypeEnum,
  PredefinedAction,
  UserModel,
} from 'api/model';
import AuthorizedTextButton from 'components/inputs/AuthorizedTextButton';
import useMe from 'contexts/authentication/useMe';
import useTranslations from 'contexts/basicData/useTranslations';
import styled from 'styled-components';
import Roles, { includesRoles } from 'constants/Roles';
import PredefinedActionModal from './PredefinedActionModal';
import { useAssignmentGetCarTransportTruckAssignmentsForAssignment } from 'api/assignment/assignment';

const EllipsisWrapper = styled.div`
  display: block;
  color: ${({ theme }) => theme.colors.background.button};
  padding: 5px;
`;

const nonStopOverableAssignmentStatuses: Set<AssignmentStatusEnum> = new Set([
  AssignmentStatusEnum.Created,
  AssignmentStatusEnum.Planned,
  AssignmentStatusEnum.Assigned,
  AssignmentStatusEnum.Accepted,
  AssignmentStatusEnum.Complete,
  AssignmentStatusEnum.Approved,
  AssignmentStatusEnum.Canceled,
  AssignmentStatusEnum.Aborted,
  AssignmentStatusEnum.Discarded,
]);

type Props = {
  assignment: AssignmentModel;
  caseModel: CaseModel;
  assignmentStatusID: AssignmentStatusEnum;
  editMode: boolean;
  currentCaseType: CaseTypeEnum;
  assignedTo?: UserModel;
  drivingPolicyAssignmentInNeedOfHandling: boolean | undefined;
  onEditClick: (assignment: AssignmentModel) => void;
  onStopOverClick: (assignmentID: number) => void;
  onPlannedStopOverClick: (assignmentID: number, preselectedCompanyId?: number) => void;
};

const PredefinedActionMenu: React.FC<Props> = ({
  assignment,
  caseModel,
  assignmentStatusID,
  editMode,
  currentCaseType,
  assignedTo,
  drivingPolicyAssignmentInNeedOfHandling,

  onEditClick,
  onStopOverClick,
  onPlannedStopOverClick,
}) => {
  const loggedInUser = useMe();
  const { predefinedActions } = useTranslations();

  // api
  const getCarTransportTruckAssignmentsForAssignmentCall =
    useAssignmentGetCarTransportTruckAssignmentsForAssignment(
      {
        assignmentId: assignment.assignmentID,
      },
      {
        query: {
          enabled: (
            [
              // only show for revelant case types
              CaseTypeEnum.InternalDelivery,
              CaseTypeEnum.InternalDeliverySales,
              CaseTypeEnum.FacilityToFacility,
              CaseTypeEnum.FacilityTransfer,
            ] as CaseTypeEnum[]
          ).includes(currentCaseType),
        },
      }
    );

  // state
  const [showPredefinedActions, setShowPredefinedActions] = useState(false);
  const [predefinedActionType, setPredefinedActionType] =
    useState<PredefinedAction>();

  return (
    <>
      <EllipsisWrapper
        onBlur={() => {
          if (showPredefinedActions) {
            setShowPredefinedActions(false);
          }
        }}
        onFocus={() => {
          if (
            !showPredefinedActions &&
            assignmentStatusID !== AssignmentStatusEnum.Discarded
          ) {
            setShowPredefinedActions(true);
          }
        }}
        onMouseLeave={() => {
          if (showPredefinedActions) {
            setShowPredefinedActions(false);
          }
        }}
        onMouseOver={() => {
          if (
            !showPredefinedActions &&
            assignmentStatusID !== AssignmentStatusEnum.Discarded
          ) {
            setShowPredefinedActions(true);
          }
        }}
      >
        {loggedInUser?.roles.includes(Roles.Admin) && (
          <span style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
            <FontAwesomeIcon icon={faRunning} /> Snabbval
          </span>
        )}
        {showPredefinedActions && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              position: 'absolute',
              right: 0,
              border: '1px solid black',
              borderRadius: '3px',
              backgroundColor: 'white',
              padding: '6px',
              alignItems: 'flex-start',
            }}
          >
            {includesRoles(loggedInUser?.roles ?? [], [Roles.Admin]) &&
              assignedTo && (
                <>
                  <h1>Annonsbil</h1>
                  {assignment.assignmentRequests.length === 0 ? (
                    <AuthorizedTextButton
                      disabled={
                        assignmentStatusID > AssignmentStatusEnum.Started
                      }
                      onClick={() => {
                        setPredefinedActionType(
                          PredefinedAction.REQUEST_INTERNAL_TRANSPORT
                        );
                      }}
                    >
                      {
                        predefinedActions[
                          PredefinedAction.REQUEST_INTERNAL_TRANSPORT
                        ]
                      }
                    </AuthorizedTextButton>
                  ) : (
                    <AuthorizedTextButton
                      disabled={
                        assignmentStatusID > AssignmentStatusEnum.Started ||
                        (
                          [
                            AssignmentRequestStatusEnum.Denied,
                            AssignmentRequestStatusEnum.Removed,
                          ] as AssignmentRequestStatusEnum[]
                        ).includes(
                          assignment.assignmentRequests[0]
                            ?.assignmentRequestStatusID
                        )
                      }
                      onClick={() =>
                        setPredefinedActionType(
                          PredefinedAction.CANCEL_INTERNAL_TRANSPORT_REQUEST
                        )
                      }
                    >
                      {
                        predefinedActions[
                          PredefinedAction.CANCEL_INTERNAL_TRANSPORT_REQUEST
                        ]
                      }
                    </AuthorizedTextButton>
                  )}
                </>
              )}
            {currentCaseType === CaseTypeEnum.InternalDelivery && (
              <>
                <h1>Lastbilstransport</h1>
                {getCarTransportTruckAssignmentsForAssignmentCall.data
                  ?.length === 0 ? (
                  <AuthorizedTextButton
                    onClick={() => {
                      setPredefinedActionType(
                        PredefinedAction.ASSIGN_TO_CAR_TRANSPORT_TRUCK
                      );
                    }}
                  >
                    Tilldela till Lastbilstransport
                  </AuthorizedTextButton>
                ) : (
                  <AuthorizedTextButton
                    onClick={() => {
                      setPredefinedActionType(
                        PredefinedAction.REMOVE_FROM_CAR_TRANSPORT_TRUCK
                      );
                    }}
                  >
                    Ta bort från Lastbilstransport
                  </AuthorizedTextButton>
                )}
              </>
            )}
            <h1>Snabbvaladdress</h1>
            {editMode && (
              <AuthorizedTextButton onClick={() => onEditClick(assignment)}>
                Redigera
              </AuthorizedTextButton>
            )}

            {!nonStopOverableAssignmentStatuses.has(assignmentStatusID) && (
              <AuthorizedTextButton
                onClick={() => onStopOverClick(assignment.assignmentID)}
              >
                Mellanlanda
              </AuthorizedTextButton>
            )}

            {(assignmentStatusID === AssignmentStatusEnum.Created ||
              assignmentStatusID === AssignmentStatusEnum.Planned) && (
              <AuthorizedTextButton
                onClick={() => onPlannedStopOverClick(assignment.assignmentID)}
              >
                Planerad mellanlandning
              </AuthorizedTextButton>
            )}

            {currentCaseType === CaseTypeEnum.InternalDelivery &&
              assignmentStatusID < 1000 && (
                <>
                  <AuthorizedTextButton
                    onClick={() => onPlannedStopOverClick(assignment.assignmentID, 16)}
                  >
                    Mellanlanda Zäkra Hub Jakobsberg
                  </AuthorizedTextButton>
                  <AuthorizedTextButton
                    onClick={() => onPlannedStopOverClick(assignment.assignmentID, 116)}
                  >
                    Mellanlanda Zäkra Hub Norsborg
                  </AuthorizedTextButton>
                </>
              )}

            <AuthorizedTextButton
              onClick={() =>
                setPredefinedActionType(PredefinedAction.ORDER_TO_KUNGSANGEN)
              }
            >
              {predefinedActions[PredefinedAction.ORDER_TO_KUNGSANGEN]}
            </AuthorizedTextButton>

            <AuthorizedTextButton
              onClick={() =>
                setPredefinedActionType(PredefinedAction.PARK_ZAKRA_OFFICE)
              }
            >
              {predefinedActions[PredefinedAction.PARK_ZAKRA_OFFICE]}
            </AuthorizedTextButton>
            <AuthorizedTextButton
              onClick={() =>
                setPredefinedActionType(PredefinedAction.PARK_RM_LOGISTICS)
              }
            >
              {predefinedActions[PredefinedAction.PARK_RM_LOGISTICS]}
            </AuthorizedTextButton>
            {drivingPolicyAssignmentInNeedOfHandling && (
              <>
                <h1>Körtidspolicy</h1>
                <AuthorizedTextButton
                  onClick={() =>
                    setPredefinedActionType(
                      PredefinedAction.REQUEST_OVERRIDE_DRIVING_POLICY
                    )
                  }
                >
                  {
                    predefinedActions[
                      PredefinedAction.REQUEST_OVERRIDE_DRIVING_POLICY
                    ]
                  }
                </AuthorizedTextButton>
              </>
            )}
          </div>
        )}
      </EllipsisWrapper>
      {predefinedActionType !== undefined && (
        <PredefinedActionModal
          assignment={assignment}
          caseModel={caseModel}
          caseType={currentCaseType}
          onClose={() => setPredefinedActionType(undefined)}
          actionType={predefinedActionType}
        />
      )}
    </>
  );
};

export default PredefinedActionMenu;
